import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Boxes,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  DollarSign,
  Loader2,
  Search,
  TrendingDown,
} from 'lucide-react';
import { useInventarioBodega } from '../hooks/useIngredientes';
import IngredienteLotesPanel from './IngredienteLotesPanel';
import type { BodegaIngredienteRow } from '../services/ingredientesService';
import { formatearFechaCorta } from '../utils/dateUtils';
import { leyendaValorizacion } from '@suminuta/shared';

const fmtCantidad = (n: number, dec = 2) =>
  n.toLocaleString('es-CL', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });

const fmtDinero = (n: number) =>
  n.toLocaleString('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const fmtFecha = (iso: string | null) => {
  if (!iso) return null;
  return formatearFechaCorta(iso);
};

const labelEstadoStock: Record<BodegaIngredienteRow['estado_stock'], string> = {
  ok: 'OK',
  bajo: 'Stock bajo',
  agotado: 'Agotado',
};

const badgeEstadoStock: Record<BodegaIngredienteRow['estado_stock'], string> = {
  ok: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  bajo: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  agotado: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const OPCIONES_POR_PAGINA = [10, 25, 50, 100];

export const VistaBodega: React.FC = () => {
  const { data, isLoading, error } = useInventarioBodega();
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<string>('');
  const [expandido, setExpandido] = useState<number | null>(null);
  const [porPagina, setPorPagina] = useState<number>(10);
  const [pagina, setPagina] = useState<number>(1);

  const filtrados = useMemo(() => {
    if (!data) return [];
    let list = data.ingredientes;
    if (busqueda.trim()) {
      const q = busqueda.trim().toLowerCase();
      list = list.filter(
        (i) =>
          i.nombre.toLowerCase().includes(q) ||
          (i.categoria || '').toLowerCase().includes(q)
      );
    }
    if (filtroEstado) {
      if (filtroEstado === 'con_vencer') {
        list = list.filter(
          (i) => i.lotes_por_vencer > 0 || i.lotes_vencidos > 0
        );
      } else {
        list = list.filter((i) => i.estado_stock === filtroEstado);
      }
    }
    return list;
  }, [data, busqueda, filtroEstado]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));

  // Ajustar página si los filtros reducen el total.
  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [totalPaginas, pagina]);

  // Al cambiar filtros o tamaño de página, volver al inicio.
  useEffect(() => {
    setPagina(1);
  }, [busqueda, filtroEstado, porPagina]);

  const inicio = (pagina - 1) * porPagina;
  const fin = inicio + porPagina;
  const pagedRows = filtrados.slice(inicio, fin);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6 flex items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-blue-600 dark:text-blue-400" />
        <span className="text-sm text-gray-600 dark:text-gray-400">
          Cargando inventario de bodega…
        </span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-red-200/60 dark:border-red-800/60 p-6 flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
        <span className="text-sm text-red-700 dark:text-red-300">
          No se pudo cargar el inventario de bodega.
        </span>
      </div>
    );
  }

  const { totales } = data;

  return (
    <div className="space-y-6">
      {/* Banner de totales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <TotalCard
          icon={<Boxes className="h-6 w-6 text-blue-600 dark:text-blue-400" />}
          iconBg="bg-blue-100 dark:bg-blue-900/30"
          label="Ingredientes"
          value={totales.total_ingredientes.toLocaleString('es-CL')}
          sub={`${totales.lotes_activos.toLocaleString('es-CL')} lotes activos`}
        />
        <TotalCard
          icon={
            <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400" />
          }
          iconBg="bg-green-100 dark:bg-green-900/30"
          label="Valorización"
          value={fmtDinero(totales.valorizacion_total)}
          sub={leyendaValorizacion({
            lotes_a_referencia: totales.lotes_a_referencia ?? 0,
            ingredientes_sin_costo: 0,
            stock_sin_lote: totales.ingredientes_stock_sin_lote ?? 0,
          })}
        />
        <TotalCard
          icon={<Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />}
          iconBg="bg-yellow-100 dark:bg-yellow-900/30"
          label="Por vencer (7 d)"
          value={totales.lotes_por_vencer.toLocaleString('es-CL')}
          sub="lotes"
        />
        <TotalCard
          icon={
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
          }
          iconBg="bg-red-100 dark:bg-red-900/30"
          label="Vencidos"
          value={totales.lotes_vencidos.toLocaleString('es-CL')}
          sub="lotes"
        />
        <TotalCard
          icon={
            <TrendingDown className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          }
          iconBg="bg-orange-100 dark:bg-orange-900/30"
          label="Alertas stock"
          value={(
            totales.ingredientes_stock_bajo + totales.ingredientes_agotados
          ).toLocaleString('es-CL')}
          sub={`${totales.ingredientes_stock_bajo} bajos · ${totales.ingredientes_agotados} agotados`}
        />
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-4">
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre o categoría…"
              className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 md:max-w-xs"
          >
            <option value="">Todos los estados</option>
            <option value="ok">Stock OK</option>
            <option value="bajo">Stock bajo</option>
            <option value="agotado">Agotados</option>
            <option value="con_vencer">Con lotes por vencer/vencidos</option>
          </select>
          <div className="md:ml-auto text-sm text-gray-600 dark:text-gray-400">
            {filtrados.length} de {data.ingredientes.length} ingredientes
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/40">
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <th className="px-4 py-3 w-8"></th>
                <th className="px-4 py-3">Ingrediente</th>
                <th className="px-4 py-3 text-right">Stock</th>
                <th className="px-4 py-3 text-right">Lotes</th>
                <th className="px-4 py-3 text-right">Valorización</th>
                <th className="px-4 py-3 text-right">Costo prom.</th>
                <th className="px-4 py-3 text-center">Estado</th>
                <th className="px-4 py-3">Alertas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filtrados.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="text-center py-8 text-sm text-gray-500 dark:text-gray-400"
                  >
                    No hay ingredientes que coincidan con el filtro.
                  </td>
                </tr>
              )}
              {pagedRows.map((ing) => {
                const isExpanded = expandido === ing.id;
                const tieneAlerta =
                  ing.lotes_vencidos > 0 || ing.lotes_por_vencer > 0;
                return (
                  <React.Fragment key={ing.id}>
                    <tr
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors"
                      onClick={() => setExpandido(isExpanded ? null : ing.id)}
                    >
                      <td className="px-4 py-3">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {ing.nombre}
                        </div>
                        {ing.categoria && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {ing.categoria}
                          </div>
                        )}
                      </td>
                      <td
                        className="px-4 py-3 text-right"
                        title={`${fmtCantidad(ing.stock_actual_base, 0)} ${ing.unidad_base_simbolo ?? ''}`}
                      >
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {fmtCantidad(ing.stock_actual, 2)}{' '}
                          {ing.unidad_display_simbolo ?? ''}
                        </div>
                        {ing.stock_minimo > 0 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            mín: {fmtCantidad(ing.stock_minimo, 2)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {ing.lotes_activos}
                        </div>
                        {ing.total_disponible_base > 0 &&
                          ing.unidad_base_simbolo && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {fmtCantidad(ing.total_disponible_base, 0)}{' '}
                              {ing.unidad_base_simbolo}
                            </div>
                          )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-green-700 dark:text-green-400">
                        {ing.valorizacion_total > 0
                          ? fmtDinero(ing.valorizacion_total)
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">
                        {ing.costo_promedio_base > 0
                          ? `${fmtDinero(ing.costo_promedio_base)} / ${ing.unidad_base_simbolo}`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badgeEstadoStock[ing.estado_stock]}`}
                        >
                          {labelEstadoStock[ing.estado_stock]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {!tieneAlerta && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            —
                          </span>
                        )}
                        <div className="flex flex-wrap gap-1">
                          {ing.lotes_por_vencer > 0 && (
                            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                              {ing.lotes_por_vencer} por vencer
                            </span>
                          )}
                          {ing.lotes_vencidos > 0 && (
                            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                              {ing.lotes_vencidos} vencidos
                            </span>
                          )}
                        </div>
                        {ing.proximo_vencimiento && ing.lotes_por_vencer > 0 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            próximo: {fmtFecha(ing.proximo_vencimiento)}
                          </div>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50 dark:bg-gray-900/40">
                        <td colSpan={8} className="px-4 py-4">
                          <IngredienteLotesPanel ingredienteId={ing.id} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {filtrados.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span>Mostrando</span>
              <select
                value={porPagina}
                onChange={(e) => setPorPagina(parseInt(e.target.value))}
                className="px-2 py-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {OPCIONES_POR_PAGINA.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span>
                de {filtrados.length}
                {filtrados.length !== 1 ? ' ingredientes' : ' ingrediente'}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setPagina(1)}
                disabled={pagina === 1}
                className="p-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Primera página"
              >
                <ChevronsLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPagina((p) => Math.max(1, p - 1))}
                disabled={pagina === 1}
                className="p-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Página anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                Página {pagina} de {totalPaginas}
              </span>
              <button
                onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                disabled={pagina >= totalPaginas}
                className="p-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Página siguiente"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPagina(totalPaginas)}
                disabled={pagina >= totalPaginas}
                className="p-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Última página"
              >
                <ChevronsRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface TotalCardProps {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  value: string;
  sub?: string;
}

const TotalCard: React.FC<TotalCardProps> = ({
  icon,
  iconBg,
  label,
  value,
  sub,
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-4">
    <div className="flex items-start">
      <div className={`${iconBg} p-3 rounded-lg shrink-0`}>{icon}</div>
      <div className="ml-3 min-w-0">
        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
          {label}
        </p>
        <p className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate">
          {value}
        </p>
        {sub && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
            {sub}
          </p>
        )}
      </div>
    </div>
  </div>
);

export default VistaBodega;
